/*
 * Copyright (C) 2026 - present Marek Kuzora - All Rights Reserved.
 */


import Entity from 'fierry/data/entity';
import Collection from 'fierry/data/collection';
import Lookup from 'fierry/data/lookup';

import User from 'solis/data/board/user';
import Colony from 'solis/data/board/colony';

import Settings from 'solis/data/settings';


export default class Board extends Entity
{
  static children = [ 'accountViews', 'users', 'colonies', 'zones' ];


  loadSettings (settings)
  {
    this.getSettings().load(settings.cass);
  }


  loadAccountView (cass)
  {
    return this.getAccountViews().load(cass.id, cass);
  }


  loadUser (cass)
  {
    return this.getUsers().load(cass.id, cass);
  }


  loadColony (cass)
  {
    return this.getColonies().load(cass.id, cass);
  }


  loadAnchor (cass)
  {
    const anchor = this.getColony(cass.sourceColony).loadOutboundAnchor(cass);

    this.getColony(cass.targetColony).loadInboundAnchor(cass);

    return anchor;
  }


  loadSector (cass)
  {
    return this.getColony(cass.colony).loadSector(cass);
  }


  loadZone (cass)
  {
    const zone = this.getColony(cass.colony).loadZone(cass);

    this.getZones().load(zone.getAbsoluteHex(), [ cass.colony, cass.sector, cass.id ]);

    return zone;
  }


  loadInfluence (cass)
  {
    const influence = this.getZone(cass.targetZone).loadInfluence(cass);

    this.getColony(cass.sourceColony).loadInfluence(cass);

    return influence;
  }


  unloadAccountView (cass)
  {
    this.getAccountViews().unload(cass.id);
  }


  unloadUser (cass)
  {
    this.getUsers().unload(cass.id);
  }


  unloadColony (cass)
  {
    this.getColonies().unload(cass.id);
  }


  unloadAnchor (cass)
  {
    this.getColony(cass.sourceColony).unloadOutboundAnchor(cass);
    this.getColony(cass.targetColony).unloadInboundAnchor(cass);
  }


  unloadSector (cass)
  {
    this.getColony(cass.colony).unloadSector(cass);
  }


  unloadZone (cass)
  {
    const zone = this.getColony(cass.colony).getSector(cass.sector).getZone(cass.id);

    this.getZones().unload(zone.getAbsoluteHex());

    this.getColony(cass.colony).unloadZone(cass);
  }


  unloadInfluence (cass)
  {
    this.getZone(cass.targetZone).unloadInfluence(cass);
    this.getColony(cass.sourceColony).unloadInfluence(cass);
  }


  createColony (colony)
  {
    const cass = {};

    cass.galaxy = this.cass.galaxy;
    cass.board = this.cass.id;

    cass.id = colony.cass.id;
    cass.user = colony.cass.user;

    cass.authority = 0;

    cass.totalInfluence = 0;
    cass.currentInfluence = 0;

    cass.attachDate = null;

    return this.loadColony(cass);
  }


  createAccountView (account)
  {
    const cass = {};

    cass.galaxy = this.cass.galaxy;
    cass.board = this.cass.id;

    cass.id = account.cass.id;
    cass.name = account.cass.name;
    cass.theme = account.cass.theme;

    return this.loadAccountView(cass);
  }


  createUser (id)
  {
    const cass = {};

    cass.galaxy = this.cass.galaxy;
    cass.board = this.cass.id;

    cass.id = id;

    return this.loadUser(cass);
  }


  getSettings ()
  {
    return new Settings(this.cursor.child('settings'));
  }


  getAccountView (id)
  {
    return this.getAccountViews().at(id);
  }


  getAccountViews ()
  {
    return new Collection(Entity, this.cursor.child('accountViews'), this);
  }


  getUser (id)
  {
    return this.getUsers().at(id);
  }


  getUsers ()
  {
    return new Collection(User, this.cursor.child('users'), this);
  }


  getColony (id)
  {
    return this.getColonies().at(id);
  }


  getColonies ()
  {
    return new Collection(Colony, this.cursor.child('colonies'), this);
  }


  getZone (absoluteHex)
  {
    return this.getZones().at(absoluteHex);
  }


  getZones ()
  {
    return new Lookup(this.cursor.child('zones'), ([ colony, sector, zone ]) =>
    {
      return this.getColony(colony).getSector(sector).getZone(zone);
    });
  }


};
